import React from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { Dimmer, Loader, Divider, Grid, Icon } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { baseUrlApi } from '@/routes';
import { useEntiteStore } from '@/zustand';
import COLORS from '@/constants/colors';

import ActeModal from '../../Modals/ActeModal';

interface StatistiqueMois {
  mois: string;
  naissances: number;
  reconnaissances: number;
  mariages: number;
  décès: number;
  décisions: number;
  pacs: number;
  mentions: number;
}

interface BlocTableauStatistiquesProps {
  selectedYear: number;
}

const MOIS_ABBR = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const BlocTableauStatistiques = (props: BlocTableauStatistiquesProps) => {
  const { selectedYear } = props;

  const [anneeSelected, setAnneeSelected] = React.useState<number | null>(null);
  const [moisSelected, setMoisSelected] = React.useState<number | null>(null);
  const [typeActeSelected, setTypeActeSelected] = React.useState<string | null>(
    null,
  );
  const [openActeModal, setOpenActeModal] = React.useState(false);
  const [titleModal, setTitleModal] = React.useState('');
  const [statistiques, setStatistiques] = React.useState<StatistiqueMois[]>([]);
  const [loading, setLoading] = React.useState(false);
  const blocked = useEntiteStore((state) => state.blocked);

  React.useEffect(() => {
    if (!blocked) {
      setLoading(true);
      request
        .get(`${baseUrlApi}/numerisation-actes/statistiques/${selectedYear}`)
        .then((response: any) => {
          setStatistiques(response);
        })
        .catch(() => {
          setStatistiques([]);
          toast.error(
            `Impossible de charger les statistiques de l'année ${selectedYear}`,
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedYear, blocked]);

  const totals = React.useMemo(
    () => ({
      naissances: statistiques.map((s) => s.naissances),
      reconnaissances: statistiques.map((s) => s.reconnaissances),
      mariages: statistiques.map((s) => s.mariages),
      deces: statistiques.map((s) => s.décès || 0),
      decisions: statistiques.map((s) => s.décisions || 0),
      pacs: statistiques.map((s) => s.pacs),
      mentions: statistiques.map((s) => s.mentions),
    }),
    [statistiques],
  );

  const LABEL_TO_TYPE: Record<string, string> = {
    Naissances: 'naissance',
    Reconnaissances: 'reconnaissance',
    Mariages: 'mariage',
    Décès: 'deces',
    Décisions: 'decision',
    PACS: 'pacs',
    Mentions: 'mention',
  };

  const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

  const rowStyle = {
    style: {
      padding: 0,
    },
  };

  const cellStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  };

  const columnDividerStyle = {
    borderRight: '1px solid #d4d4d5',
    width: 50,
    height: 40,
    padding: 0,
    margin: 0,
  };

  const dividerStyle = {
    style: {
      margin: 0,
      backgroundColor: 'lightgrey',
    },
  };

  const clickableStyle = {
    cursor: 'pointer',
    textDecoration: 'underline',
  };

  const renderStatValue = (
    value: number,
    monthIndex: number,
    label: string,
    bold = false,
    clickable = true,
  ) => {
    const isClickable = clickable && value > 0;

    const handleClick = () => {
      if (isClickable) {
        const isTotalAnnee = monthIndex === -1;
        const typeActe = LABEL_TO_TYPE[label];

        setAnneeSelected(selectedYear);
        setMoisSelected(isTotalAnnee ? null : monthIndex + 1);
        setTypeActeSelected(typeActe);
        setOpenActeModal(true);

        const nomMois = !isTotalAnnee
          ? dayjs(
              `${selectedYear}-${String(monthIndex + 1).padStart(2, '0')}-01`,
            ).format('MMMM')
          : '';
        setTitleModal(
          isTotalAnnee
            ? `Total des actes de ${label.toLowerCase()} en ${selectedYear}`
            : `Actes de ${label.toLowerCase()} en ${nomMois} ${selectedYear}`,
        );
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    };

    const content = (
      <span
        style={isClickable ? clickableStyle : undefined}
        onClick={isClickable ? handleClick : undefined}
        onKeyDown={isClickable ? handleKeyDown : undefined}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
      >
        {value}
      </span>
    );

    return bold ? <b>{content}</b> : content;
  };

  return (
    <>
      {loading && (
        <Dimmer active inverted>
          <Loader content="Chargement des statistiques..." />
        </Dimmer>
      )}
      <Grid
        style={{
          margin: '0 0 0 1.5em',
        }}
      >
        {/* Header Row */}
        <Grid.Row {...rowStyle}>
          <Grid.Column style={{ flex: 1 }} />
          {MOIS_ABBR.map((m, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-size list (months), order never changes
              key={i}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                <b>{m}</b>
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              <b>Total</b>
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Naissances */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <Icon name="Baby" iconSet="Lucide" color={COLORS.BlueTextColor} />
            &nbsp;
            <b>Nombre de naissances</b>
          </Grid.Column>
          {totals.naissances.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-size list (months), order never changes
              key={i}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                {renderStatValue(v, i, 'Naissances', true)}
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(sum(totals.naissances), -1, 'Naissances', true)}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Reconnaissances */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <Icon
              name="UserCheck"
              iconSet="Lucide"
              color={COLORS.GreenTextColor}
            />
            &nbsp;
            <b>Nombre de reconnaissances</b>
          </Grid.Column>
          {totals.reconnaissances.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-size list (months), order never changes
              key={i}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                {renderStatValue(v, i, 'Reconnaissances', true)}
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(
                sum(totals.reconnaissances),
                -1,
                'Reconnaissances',
                true,
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Mariages */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <Icon name="Heart" iconSet="Lucide" color={COLORS.PinkTextColor} />
            &nbsp;
            <b>Nombre de mariages</b>
          </Grid.Column>
          {totals.mariages.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-size list (months), order never changes
              key={i}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                {renderStatValue(v, i, 'Mariages', true)}
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(sum(totals.mariages), -1, 'Mariages', true)}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Décès */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <Icon name="Cross" iconSet="Lucide" color={COLORS.GreyTextColor} />
            &nbsp;
            <b>Nombre de décès</b>
          </Grid.Column>
          {totals.deces.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-size list (months), order never changes
              key={i}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                {renderStatValue(v, i, 'Décès', true)}
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(sum(totals.deces), -1, 'Décès', true)}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Décisions */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <Icon
              name="BookmarkCheck"
              iconSet="Lucide"
              color={COLORS.MarinBlueTextColor}
            />
            &nbsp;
            <b>Nombre de décisions</b>
          </Grid.Column>
          {totals.decisions.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-size list (months), order never changes
              key={i}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                {renderStatValue(v, i, 'Décisions', true)}
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(sum(totals.decisions), -1, 'Décisions', true)}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* PACS */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <Icon
              name="Users"
              iconSet="Lucide"
              color={COLORS.PurpleTextColor}
            />
            &nbsp;
            <b>Nombre de PACS</b>
          </Grid.Column>
          {totals.pacs.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-size list (months), order never changes
              key={i}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>{renderStatValue(v, i, 'PACS', true)}</div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(sum(totals.pacs), -1, 'PACS', true)}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Mentions */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <Icon
              name="FileText"
              iconSet="Lucide"
              color={COLORS.OrangeTextColor}
            />
            &nbsp;
            <b>Nombre de mentions</b>
          </Grid.Column>
          {totals.mentions.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-size list (months), order never changes
              key={i}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                {renderStatValue(v, i, 'Mentions', true, false)}
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(
                sum(totals.mentions),
                -1,
                'Mentions',
                true,
                false,
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <ActeModal
        title={titleModal}
        annee={anneeSelected}
        mois={moisSelected}
        typeActe={typeActeSelected as any}
        open={openActeModal}
        onClose={() => {
          setAnneeSelected(null);
          setMoisSelected(null);
          setTypeActeSelected(null);
          setOpenActeModal(false);
        }}
      />
    </>
  );
};

export default BlocTableauStatistiques;
